import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Client, CrmLead, datetimeLocalToIso, isoToDatetimeLocalValue } from '../services/crmService';
import { leadSuggestionService } from '../services/leadSuggestionService';
import {
  emailService,
  EmailAccountConnection,
  EMAIL_TEMPLATE_CATEGORIES,
  EmailTemplate,
  EmailTemplateCategory,
  OutboundEmailRow,
} from '../services/emailService';
import { ApiError } from '../services/apiClient';
import { applyEmailTemplateVars } from '../utils/emailTemplateVars';
import { followUpReplySubject } from '../utils/emailSubject';
import {
  followUpReminderPrefill,
  suggestedFollowUpDatetimeLocal,
} from '../utils/followUpDefaults';
import { getReconnectErrorMeta } from '../utils/emailErrors';

interface EmailComposeModalProps {
  open: boolean;
  onClose: () => void;
  contact: Client | CrmLead;
  /** Pipeline CRM Client vs marketing `crm.Lead` */
  contactKind: 'client' | 'lead';
  followUpContext: 'lead' | 'prospect' | 'client';
  onSent?: () => void;
  editScheduledEmail?: OutboundEmailRow | null;
}

const EMAIL_SUGGESTION_TYPES = [
  { value: 'intro', label: 'Intro Email' },
  { value: 'follow_up', label: 'Follow-Up Email' },
  { value: 're_engagement', label: 'Re-engagement Email' },
  { value: 'estimate_follow_up', label: 'Estimate Follow-Up' },
  { value: 'appointment_request', label: 'Appointment Request' },
] as const;

const toDatetimeLocalValue = (date: Date) => {
  const pad = (v: number) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const EmailComposeModal: React.FC<EmailComposeModalProps> = ({
  open,
  onClose,
  contact,
  contactKind,
  followUpContext,
  onSent,
  editScheduledEmail = null,
}) => {
  const isEditMode = Boolean(editScheduledEmail);
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<EmailAccountConnection[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [connectionId, setConnectionId] = useState<string>('');
  const [templateId, setTemplateId] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [sendMode, setSendMode] = useState<'now' | 'scheduled'>('now');
  const [scheduledSendAt, setScheduledSendAt] = useState('');
  const [reminderAt, setReminderAt] = useState('');
  const [stepNumber, setStepNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reconnectRequired, setReconnectRequired] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateCategory, setSaveTemplateCategory] = useState<EmailTemplateCategory>('general');
  const [saveTemplateShared, setSaveTemplateShared] = useState(false);
  const [saveTemplateDescription, setSaveTemplateDescription] = useState('');
  const [saveTemplateDelayDays, setSaveTemplateDelayDays] = useState('');
  const [reminderEdited, setReminderEdited] = useState(false);
  const [suggestionType, setSuggestionType] = useState<(typeof EMAIL_SUGGESTION_TYPES)[number]['value']>('intro');
  const [suggestionCta, setSuggestionCta] = useState('');
  const [generatingSuggestion, setGeneratingSuggestion] = useState(false);

  const load = useCallback(async () => {
    try {
      const ac = await emailService.listAccounts();
      setAccounts(ac);
      if (isEditMode && editScheduledEmail) {
        const tm = await emailService.listTemplates({ context: followUpContext, is_active: true });
        setTemplates(tm);
        if (editScheduledEmail.connection_id) {
          setConnectionId(editScheduledEmail.connection_id);
        } else {
          const owned = ac.filter((a) => a.is_owned_by_current_user);
          const primary =
            owned.find((a) => a.is_primary) || owned[0] || ac.find((a) => a.is_primary) || ac[0];
          if (primary) setConnectionId(primary.id);
        }
        return;
      }
      const summaryParams =
        contactKind === 'client' ? { client_id: contact.id } : { lead_id: contact.id };
      const [tm, summary] = await Promise.all([
        emailService.listTemplates({ context: followUpContext, is_active: true }),
        emailService.getCommunicationSummary(summaryParams),
      ]);
      setTemplates(tm);
      const owned = ac.filter((a) => a.is_owned_by_current_user);
      const primary =
        owned.find((a) => a.is_primary) || owned[0] || ac.find((a) => a.is_primary) || ac[0];
      if (primary) setConnectionId(primary.id);
      const lastOutbound = summary.last_outbound;
      if (lastOutbound?.subject?.trim()) {
        setSubject(followUpReplySubject(lastOutbound.subject));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load email settings');
    }
  }, [followUpContext, contactKind, contact.id, isEditMode, editScheduledEmail]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setReconnectRequired(false);
    setSuggestionCta('');
    setTemplateId('');
    setSuggestionType('intro');
    setReminderEdited(false);
    const contactTouches = (contact as Client | CrmLead).touches ?? 0;
    if (isEditMode && editScheduledEmail) {
      setSubject(editScheduledEmail.subject || '');
      setBody(editScheduledEmail.body || '');
      setCc(editScheduledEmail.cc || '');
      setBcc(editScheduledEmail.bcc || '');
      setSendMode('scheduled');
      setScheduledSendAt(
        editScheduledEmail.scheduled_send_at
          ? isoToDatetimeLocalValue(editScheduledEmail.scheduled_send_at)
          : ''
      );
      setReminderAt(
        followUpReminderPrefill((contact as Client | CrmLead).next_follow_up_at, contactTouches)
      );
      setStepNumber(editScheduledEmail.step_number || contactTouches + 1);
    } else {
      setSubject('');
      setBody('');
      setCc('');
      setBcc('');
      setSendMode('now');
      setScheduledSendAt('');
      setReminderAt(
        followUpReminderPrefill((contact as Client | CrmLead).next_follow_up_at, contactTouches)
      );
      setStepNumber(contactTouches + 1);
    }
    load();
  }, [open, load, contact, isEditMode, editScheduledEmail]);

  const applyTemplate = (tid: string) => {
    const t = templates.find((x) => x.id === tid);
    if (!t || !user) return;
    setSubject(applyEmailTemplateVars(t.subject_template, contact, user));
    setBody(applyEmailTemplateVars(t.body_template, contact, user));
    if (!reminderEdited && typeof t.default_follow_up_delay_days === 'number' && t.default_follow_up_delay_days >= 0) {
      const contactTouches = (contact as Client | CrmLead).touches ?? 0;
      setReminderAt(
        suggestedFollowUpDatetimeLocal({
          touches: contactTouches,
          templateDelayDays: t.default_follow_up_delay_days,
        })
      );
    }
  };

  const handleTemplateChange = (tid: string) => {
    const hasDraft = subject.trim().length > 0 || body.trim().length > 0;
    if (tid && hasDraft) {
      const shouldReplace = window.confirm('Apply this template and replace the current draft subject/body?');
      if (!shouldReplace) {
        return;
      }
    }
    setTemplateId(tid);
    if (tid) applyTemplate(tid);
  };

  const generateSuggestion = async () => {
    setError('');
    setSuggestionCta('');
    setGeneratingSuggestion(true);
    try {
      const hasDraft = subject.trim().length > 0 || body.trim().length > 0;
      if (hasDraft) {
        const shouldReplace = window.confirm('Generate a suggestion and replace the current subject/body draft?');
        if (!shouldReplace) {
          setGeneratingSuggestion(false);
          return;
        }
      }
      const result = await leadSuggestionService.generateEmailSuggestion(
        contactKind === 'client'
          ? { client_id: contact.id, email_type: suggestionType }
          : { lead_id: contact.id, email_type: suggestionType }
      );
      setSubject(result.subject || '');
      setBody(result.body || '');
      setSuggestionCta(result.cta_suggestion || '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate suggestion');
    } finally {
      setGeneratingSuggestion(false);
    }
  };

  const handleSaveCurrentAsTemplate = async () => {
    if (!subject.trim() || !body.trim() || !saveTemplateName.trim()) {
      setError('Template name, subject, and body are required.');
      return;
    }
    try {
      setSavingTemplate(true);
      const created = await emailService.createTemplate({
        name: saveTemplateName.trim(),
        category: saveTemplateCategory,
        subject_template: subject.trim(),
        body_template: body.trim(),
        description: saveTemplateDescription.trim(),
        default_follow_up_delay_days: saveTemplateDelayDays === '' ? null : Number(saveTemplateDelayDays),
        is_shared: saveTemplateShared,
        is_active: true,
      });
      setTemplates((prev) => [created, ...prev]);
      setShowSaveTemplateModal(false);
      setSaveTemplateName('');
      setSaveTemplateCategory('general');
      setSaveTemplateShared(false);
      setSaveTemplateDescription('');
      setSaveTemplateDelayDays('');
      setTemplateId(created.id);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Save template failed';
      setError(msg);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSend = async () => {
    setError('');
    setReconnectRequired(false);
    if (!isEditMode) {
      const suppressed =
        contactKind === 'client'
          ? Boolean((contact as Client).do_not_contact)
          : Boolean((contact as CrmLead).do_not_contact);
      if (suppressed) {
        setError('This contact is marked Do Not Contact. Clear it on their profile to send.');
        return;
      }
      if (!contact.email?.trim()) {
        setError('This contact has no email address.');
        return;
      }
    }
    if (!accounts.length) {
      setError('Connect an email account in Settings → Email first.');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    setLoading(true);
    try {
      let reminderIso: string | null = null;
      let sendAtIso: string | null = null;
      if (reminderAt) {
        reminderIso = datetimeLocalToIso(reminderAt);
      }
      if (isEditMode || sendMode === 'scheduled') {
        if (!scheduledSendAt) {
          setError('Select a date/time to schedule this email.');
          setLoading(false);
          return;
        }
        sendAtIso = datetimeLocalToIso(scheduledSendAt);
        const d = sendAtIso ? new Date(sendAtIso) : null;
        if (!d || Number.isNaN(d.getTime()) || d.getTime() <= Date.now()) {
          setError('Scheduled send time must be in the future.');
          setLoading(false);
          return;
        }
      }
      if (isEditMode && editScheduledEmail) {
        await emailService.updateScheduledEmail(editScheduledEmail.id, {
          subject: subject.trim(),
          body: body.trim(),
          send_at: sendAtIso || undefined,
          cc: cc.trim(),
          bcc: bcc.trim(),
          connection_id: connectionId || null,
          reminder_at: reminderIso,
        });
      } else {
        const payload = {
          subject: subject.trim(),
          body: body.trim(),
          template_id: templateId || null,
          connection_id: connectionId || null,
          cc: cc.trim(),
          bcc: bcc.trim(),
          reminder_at: reminderIso,
          send_at: sendAtIso,
          step_number: stepNumber < 1 ? 1 : stepNumber,
        };
        if (contactKind === 'client') {
          await emailService.send({ ...payload, client_id: contact.id });
        } else {
          await emailService.send({ ...payload, lead_id: contact.id });
        }
      }
      onSent?.();
      onClose();
    } catch (e) {
      let msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : isEditMode ? 'Save failed' : 'Send failed';
      if (getReconnectErrorMeta(e).requiresReconnect) {
        msg = 'Your email account needs new permissions before sending.';
        setReconnectRequired(true);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const noEmail = !contact.email?.trim();
  const noAccount = accounts.length === 0;
  const noTemplates = templates.length === 0;
  const suppressed =
    contactKind === 'client'
      ? Boolean((contact as Client).do_not_contact)
      : Boolean((contact as CrmLead).do_not_contact);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface border border-border rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-fg">{isEditMode ? 'Edit scheduled email' : 'Send email'}</h3>
          <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm text-fg-muted">
          To: <span className="text-fg font-medium">{contact.email || '—'}</span>
        </p>

        {isEditMode ? (
          <p className="text-sm text-fg-muted">
            Scheduled for{' '}
            <span className="text-fg font-medium">
              {editScheduledEmail?.scheduled_send_at
                ? new Date(editScheduledEmail.scheduled_send_at).toLocaleString()
                : '—'}
            </span>
          </p>
        ) : null}

        {!isEditMode && noEmail && (
          <p className="text-sm text-amber-700 dark:text-amber-400 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2">
            Add an email address to this contact before sending.
          </p>
        )}
        {!isEditMode && noAccount && (
          <p className="text-sm text-amber-700 dark:text-amber-400 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2">
            No email account is connected for your organization. Ask an admin or account owner to connect one.
          </p>
        )}
        {!isEditMode && suppressed && (
          <p className="text-sm text-amber-800 dark:text-amber-200 rounded-lg bg-amber-100 dark:bg-amber-950/50 px-3 py-2">
            Suppressed: this contact is marked <strong>Do Not Contact</strong>. Clear it on their profile to send.
          </p>
        )}

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400 rounded-lg bg-red-50 dark:bg-red-950/30 px-3 py-2">
            {error}
          </p>
        )}
        {reconnectRequired && (
          <p className="text-sm text-amber-700 dark:text-amber-400 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2">
            Reconnect your mailbox in{' '}
            <a href="/settings/email" className="underline font-medium">
              Settings → Email
            </a>
            , then try sending again.
          </p>
        )}

        {accounts.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-fg mb-1">Send from</label>
            <select
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.email_address} ({a.provider})
                  {a.connected_by_name ? ` — connected by ${a.connected_by_name}` : ''}
                  {a.allow_org_sending && !a.is_owned_by_current_user ? ' — shared with organization' : ''}
                  {a.is_primary && a.is_owned_by_current_user ? ' — primary' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <label className="block text-sm font-medium text-fg">Template</label>
            <button
              type="button"
              onClick={() => setShowSaveTemplateModal(true)}
              disabled={!subject.trim() || !body.trim()}
              className="text-xs text-primary hover:underline disabled:opacity-50 disabled:no-underline"
            >
              Save as template
            </button>
          </div>
          <select
            value={templateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
          >
            <option value="">None</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {EMAIL_TEMPLATE_CATEGORIES.find((c) => c.value === t.category)?.label ?? t.category}
                {t.is_system_template ? ' (system)' : ''}
              </option>
            ))}
          </select>
          {noTemplates ? (
            <p className="mt-2 text-xs text-fg-muted">
              No templates yet.{' '}
              <a href="/settings/email" className="underline font-medium">
                Create one in Settings → Email
              </a>
              .
            </p>
          ) : null}
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <label className="block text-sm font-medium text-fg">AI suggestion</label>
            <button
              type="button"
              onClick={generateSuggestion}
              disabled={generatingSuggestion || noEmail || noAccount || suppressed}
              className="text-xs text-primary hover:underline disabled:opacity-50 disabled:no-underline"
            >
              {generatingSuggestion ? 'Generating…' : 'Generate suggestion'}
            </button>
          </div>
          <select
            value={suggestionType}
            onChange={(e) => setSuggestionType(e.target.value as (typeof EMAIL_SUGGESTION_TYPES)[number]['value'])}
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
          >
            {EMAIL_SUGGESTION_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {suggestionCta ? (
            <p className="mt-2 text-xs text-fg-muted">
              Suggested CTA: <span className="text-fg">{suggestionCta}</span>
            </p>
          ) : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            disabled={noEmail || noAccount}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm font-mono"
            disabled={noEmail || noAccount}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">Send timing</label>
          {isEditMode ? (
            <input
              type="datetime-local"
              aria-label="Scheduled send at"
              value={scheduledSendAt}
              onChange={(e) => setScheduledSendAt(e.target.value)}
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className={`rounded-lg border px-3 py-2 text-sm ${sendMode === 'now' ? 'border-primary text-primary bg-primary/10' : 'border-border text-fg'}`}
                  onClick={() => setSendMode('now')}
                >
                  Send now
                </button>
                <button
                  type="button"
                  className={`rounded-lg border px-3 py-2 text-sm ${sendMode === 'scheduled' ? 'border-primary text-primary bg-primary/10' : 'border-border text-fg'}`}
                  onClick={() => setSendMode('scheduled')}
                >
                  Schedule send
                </button>
              </div>
              {sendMode === 'scheduled' ? (
                <input
                  type="datetime-local"
                  aria-label="Scheduled send at"
                  value={scheduledSendAt}
                  onChange={(e) => setScheduledSendAt(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
                />
              ) : null}
            </>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-fg mb-1">Follow-up reminder (optional)</label>
          <input
            type="datetime-local"
            value={reminderAt}
            onChange={(e) => {
              setReminderAt(e.target.value);
              setReminderEdited(true);
            }}
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
          />
        </div>

        <details className="rounded-lg border border-border bg-surface-muted/30 p-3 group">
          <summary className="cursor-pointer text-sm font-medium text-fg list-none flex items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
            <span>Advanced</span>
            <span className="text-fg-muted text-xs group-open:rotate-180 transition-transform">▼</span>
          </summary>
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <div>
              <label className="block text-sm font-medium text-fg mb-1">Follow-up step #</label>
              <input
                type="number"
                min={1}
                value={stepNumber}
                onChange={(e) => setStepNumber(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-24 rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
                disabled={noEmail || noAccount || suppressed}
              />
              <p className="text-xs text-fg-muted mt-1">Which touch this is for reporting (default 1).</p>
            </div>
          </div>
        </details>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-xl border border-border text-fg hover:bg-surface-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={loading || (!isEditMode && (noEmail || noAccount || suppressed))}
            className="px-4 py-2 text-sm font-medium rounded-xl bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {loading ? (isEditMode ? 'Saving…' : sendMode === 'scheduled' ? 'Scheduling…' : 'Sending…') : isEditMode ? 'Save changes' : sendMode === 'scheduled' ? 'Schedule' : 'Send'}
          </button>
        </div>
      </div>
      {showSaveTemplateModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-semibold text-fg">Save as template</h4>
              <button type="button" onClick={() => setShowSaveTemplateModal(false)} className="text-fg-muted hover:text-fg">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <input
              type="text"
              value={saveTemplateName}
              onChange={(e) => setSaveTemplateName(e.target.value)}
              placeholder="Template name"
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            />
            <select
              value={saveTemplateCategory}
              onChange={(e) => setSaveTemplateCategory(e.target.value as EmailTemplateCategory)}
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            >
              {EMAIL_TEMPLATE_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
            <textarea
              value={saveTemplateDescription}
              onChange={(e) => setSaveTemplateDescription(e.target.value)}
              placeholder="Description (optional)"
              rows={3}
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            />
            <input
              type="number"
              min={0}
              max={365}
              value={saveTemplateDelayDays}
              onChange={(e) => setSaveTemplateDelayDays(e.target.value)}
              placeholder="Default delay days (optional)"
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            />
            <label className="flex items-center gap-2 text-sm text-fg">
              <input type="checkbox" checked={saveTemplateShared} onChange={(e) => setSaveTemplateShared(e.target.checked)} />
              Share with everyone in my organization
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="btn-secondary text-sm" onClick={() => setShowSaveTemplateModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary text-sm" onClick={handleSaveCurrentAsTemplate} disabled={savingTemplate}>
                {savingTemplate ? 'Saving…' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailComposeModal;
